import { useMemo } from 'react'
import { usePreferences } from '@/shell/stores/preferences'

/**
 * Per-module table grouping state, backed by user preferences so the chosen
 * "Group by" field and collapsed groups survive reloads. Pair the returned
 * `groupBy` with `groupRows(...)` and feed the resulting groups plus
 * `collapsedGroups` / `toggleGroup` straight into <DataTable>.
 *
 *   const { groupBy, setGroupBy, collapsedGroups, toggleGroup } =
 *     useTableGrouping('sales-inbox')
 *   const groups = groupBy === 'stage' ? groupRows(rows, {...}) : undefined
 *
 * `fallback` is the group-by value used before prefs hydrate / when unset
 * (default 'none' → flat table).
 */
export function useTableGrouping(moduleId: string, fallback = 'none') {
  const groupBy = usePreferences((s) => s.tableGroupBy[moduleId] ?? fallback)
  const collapsedList = usePreferences((s) => s.tableCollapsedGroups[moduleId])
  const setTableGroupBy = usePreferences((s) => s.setTableGroupBy)
  const setTableCollapsedGroups = usePreferences((s) => s.setTableCollapsedGroups)

  const collapsedGroups = useMemo(
    () => new Set(collapsedList ?? []),
    [collapsedList],
  )

  return {
    groupBy,
    setGroupBy: (value: string) => setTableGroupBy(moduleId, value),
    collapsedGroups,
    /** Replace the collapsed set wholesale (collapse all / expand all). */
    setCollapsedGroups: (keys: readonly string[]) =>
      setTableCollapsedGroups(moduleId, [...keys]),
    toggleGroup: (key: string) => {
      const next = new Set(collapsedGroups)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      setTableCollapsedGroups(moduleId, [...next])
    },
  }
}
